<template>
  <div v-if="type !== 'hidden'" class="" :class="{
    'has-error': !!errorMessage,
    'has-success': fieldMeta.valid && fieldMeta.dirty,
  }">
    <!-- Label -->
    <label v-if="label" :for="name" class="block mb-2 font-medium">
      <i v-if="icon" class="pi mr-2" :class="icon" />
      {{ label }}
      <span v-if="required" class="text-red-500 ml-1">*</span>
    </label>

    <!-- Edit Mode -->
    <template v-if="meta?.mode === 'edit'">
      <!-- Number Input -->
      <InputNumber v-if="type === 'number'" :id="name" v-model="inputValue" :name="name" :placeholder="placeholder"
        :disabled="disabled" :readonly="readOnly" :min="min" :max="max" :minFractionDigits="toFixed ?? 2"
        :maxFractionDigits="toFixed ?? 2" :class="[
          className,
          'w-full',
          {
            'p-invalid': !!errorMessage,
          },
        ]" v-bind="attrs" @blur="handleBlur" @input="handleInputChange" />

      <!-- Masked Input -->
      <InputMask v-else-if="mask" :id="name" v-model="inputValue" :name="name" :mask="mask" :placeholder="placeholder"
        :disabled="disabled" :readonly="readOnly" :class="[
          className,
          'w-full',
          {
            'p-invalid': !!errorMessage,
          },
        ]" v-bind="attrs" @blur="handleBlur" @input="handleInputChange" />

      <!-- Calendar (Date/Time) -->
      <Calendar v-else-if="
        ['date', 'time', 'datetime', 'datetime-local'].includes(type)
      " :id="name" :modelValue="calendarModel" :name="name"
        :showTime="type === 'datetime' || type === 'datetime-local'" :timeOnly="type === 'time'"
        :dateFormat="dateFormat" :placeholder="placeholder" :disabled="disabled" :readonly="readOnly" :showIcon="true"
        :class="[
          className,
          'w-full',
          {
            'p-invalid': !!errorMessage,
          },
        ]" v-bind="attrs" @blur="handleBlur" @update:modelValue="handleInputChange" />

      <!-- Text/Email/Password Input -->
      <template v-else>
        <div :class="suffix ? 'relative w-full' : undefined">
          <InputText
            :id="name"
            v-model="inputValue"
            :name="name"
            :type="type"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readOnly"
            :maxlength="maxLength"
            :class="[
              className,
              'w-full',
              { 'p-invalid': !!errorMessage },
            ]"
            v-bind="attrs"
            @blur="handleBlur"
            @input="handleInputChange"
          />
          <span
            v-if="suffix"
            class="font-normal absolute pointer-events-none"
            :style="{
              left: `calc(1.3rem + ${String(inputValue || '').length}ch)`,
              top: '52%',
              transform: 'translateY(-50%)',
            }"
            >{{ suffix }}</span
          >
        </div>
      </template>
    </template>

    <!-- View Mode -->
    <div v-else class="font-medium text-color">
      {{ formattedValue }}<template v-if="suffix">{{ suffix }}</template>
    </div>

    <!-- Error Message -->
    <small v-if="errorMessage" class="p-error block mt-1">
      {{ errorMessage }}
    </small>
  </div>

  <!-- Hidden Input -->
  <template v-else>
  <input type="hidden" :name="name" :value="inputValue" />
  <small v-if="errorMessage" class="p-error font-normal">
    {{ errorMessage }}
  </small>
</template>
  <!-- <input v-else type="hidden" :name="name" :value="inputValue" /> -->
</template>

<script>
import { useField } from "vee-validate";
import { watch, computed, inject, onMounted } from "vue";
import { Core } from "../../../helpers/core";

export default {
  name: "BaseInput",

  inject: ["meta"],

  props: {
    type: {
      type: String,
      default: "text",
      validator: (value) =>
        [
          "text",
          "email",
          "password",
          "number",
          "tel",
          "url",
          "date",
          "time",
          "datetime",
          "datetime-local",
          "hidden",
        ].includes(value),
    },
    icon: {
      type: String,
      default: "",
    },
    modelValue: {
      type: [String, Number, Date],
      default: "",
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    className: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    maxLength: {
      type: Number,
      default: 256,
    },
    mask: {
      type: String,
      default: "",
    },
    toFixed: {
      type: Number,
      default: null,
    },
    min: {
      type: Number,
      default: undefined,
    },
    max: {
      type: Number,
      default: undefined,
    },
    dateFormat: {
      type: String,
      default: "mm/dd/yy",
    },
    suffix: {
      type: String,
      default: "",
    },
  },

  emits: ["update:modelValue", "blur", "input"],

  setup(props, { emit, attrs }) {
    // Parse initial value based on type
    const parseValue = (data) => {
      if (!data) return data;

      if (["date", "time", "datetime", "datetime-local"].includes(props.type)) {
        let format = "";
        if (props.type === "date") {
          format = "YYYY-MM-DD";
        } else if (props.type === "time") {
          format = "HH:mm";
        } else if (
          props.type === "datetime" ||
          props.type === "datetime-local"
        ) {
          format = "YYYY-MM-DD HH:mm";
        }
        return Core.datey(data, format);
      }

      return data;
    };

    // Setup vee-validate field
    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      handleChange,
      resetField,
      meta: fieldMeta,
    } = useField(props.name, undefined, {
      initialValue: parseValue(props.modelValue),
    });

    const formMeta = inject("meta", null);

    onMounted(() => {
      if (formMeta?.key !== "create" || formMeta?.type === "template") return;
      if (props.type !== "date") return;
      if (inputValue.value != null && inputValue.value !== "") return;
      const today = Core.datey(new Date(), "YYYY-MM-DD");
      inputValue.value = today;
      handleChange(today);
      emit("update:modelValue", today);
    });

    // Watch for external model changes
    watch(
      () => props.modelValue,
      (newValue) => {
        const parsed = parseValue(newValue);
        if (inputValue.value !== parsed) {
          inputValue.value = parsed;
        }
      },
    );

    // Format value for display in view mode
    const formattedValue = computed(() => {
      if (!inputValue.value) return "-";

      if (props.type === "date") {
        return Core.datey(inputValue.value, "MM/DD/YYYY");
      } else if (props.type === "time") {
        return Core.datey(inputValue.value, "HH:mm A");
      } else if (props.type === "datetime" || props.type === "datetime-local") {
        return Core.datey(inputValue.value, "MMM DD, YYYY HH:mm A");
      } else if (props.type === "number") {
        return Number(inputValue.value).toFixed(props.toFixed ?? 2);
      }

      return inputValue.value;
    });
    const calendarModel = computed(() =>
      props.type === "date" && inputValue.value
        ? (Core.datey(inputValue.value, null)?.toDate?.() ?? null)
        : inputValue.value,
    );
    // Handle input changes
    const handleInputChange = (event) => {
      let value = event;

      // Handle different event types
      if (event?.target) {
        value = event.target.value;
      } else if (event?.value !== undefined) {
        value = event.value;
      }
      if (props.type === "date") {
        const apiValue = Core.datey(value, "YYYY-MM-DD");
        inputValue.value = apiValue;
        handleChange(apiValue);
        emit("update:modelValue", apiValue);
        emit("input", apiValue);
        return;
      }

      inputValue.value = value;
      handleChange(value);
      emit("update:modelValue", value);
      emit("input", value);
    };

    // Handle blur events
    const handleBlurEvent = (event) => {
      handleBlur(event);
      emit("blur", event);
    };

    return {
      inputValue,
      errorMessage,
      handleBlur: handleBlurEvent,
      handleInputChange,
      fieldMeta,
      formattedValue,
      attrs,
      calendarModel,
    };
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 1.5rem;
}

.field.has-error :deep(.p-inputtext),
.field.has-error :deep(.p-inputnumber-input),
.field.has-error :deep(.p-datepicker-input) {
  border-color: var(--red-500);
}

.field.has-error :deep(.p-inputtext:focus),
.field.has-error :deep(.p-inputnumber-input:focus),
.field.has-error :deep(.p-datepicker-input:focus) {
  border-color: var(--red-500);
  box-shadow: 0 0 0 0.2rem rgba(239, 68, 68, 0.25);
}

.field.has-success :deep(.p-inputtext),
.field.has-success :deep(.p-inputnumber-input),
.field.has-success :deep(.p-datepicker-input) {
  border-color: var(--green-500);
}

.field.has-success :deep(.p-inputtext:focus),
.field.has-success :deep(.p-inputnumber-input:focus),
.field.has-success :deep(.p-datepicker-input:focus) {
  border-color: var(--green-500);
  box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.25);
}
</style>
